import React, { useState, useEffect, useMemo, useCallback } from "react";
import { CheckCircle2, UploadCloud, Download, Layers, ChevronRight, AlertCircle, AlertTriangle, FileCheck2, Clock, RotateCcw, Loader2, Users, Coins, Trash2 } from "lucide-react";
import { C, eur } from "../igualdad-data.js";
import { apiGet, apiPost, apiPut, apiPatch, apiDelete, apiUpload, downloadCsv } from "../igualdad-api.js";
import { useIgualdad } from "../igualdad-store.jsx";
import { Status, SectionHead, Toggle } from "../igualdad-components.jsx";
import { navigate } from "../router.jsx";

/* ============================================================
   Talention — Módulo Igualdad · Carga del registro
   Seis pasos: subir, totales, situaciones, conceptos,
   confirmar y agrupaciones.

   Subir NO crea nada. El fichero queda aparcado y se revisa:
   cuánto suma, si cuadra, si las jornadas y las fechas están
   bien, y cómo se trata cada concepto. Solo el último paso
   escribe en el registro.

   Los criterios de cada concepto —normalizar por jornada,
   elevar al periodo, computar al total— se preguntan aquí
   porque nadie los trae en el fichero: en la herramienta del
   Ministerio se rellenan a mano. Se responden una vez por
   familia, no una por concepto.

   Las agrupaciones van DESPUÉS de confirmar y no antes: la
   correspondencia es sobre los grupos de convenio y los
   puestos que la propia carga da de alta, así que hasta que
   no entran no hay nada que emparejar. Mientras no se
   revisen, el entregable las marca como propuesta.
   ============================================================ */

const STEPS = ["Subir fichero", "Totales", "Situaciones", "Conceptos", "Confirmar", "Agrupaciones"];

// Cabecera de la plantilla de ejemplo. Coincide con el mapeo por defecto, de
// modo que un fichero recién descargado se mapea solo.
const PLANTILLA = [
  "ID_SITUACION", "ID_PERSONA", "SEXO", "F_INICIO", "F_FIN", "PCT_JORNADA",
  "PUESTO", "CATEGORIA", "CENTRO", "Salario base", "Plus de antigüedad", "TOTAL",
];

const CLASIFICACIONES = [
  ["salario_base", "Salario base"],
  ["complemento_personal", "Complemento personal"],
  ["complemento_puesto", "Complemento de puesto"],
  ["variable", "Variable"],
  ["especie", "Especie"],
  ["extrasalarial", "Extrasalarial"],
  ["indemnizacion", "Indemnización"],
];

/* Las ocho hojas de resultados de la Herramienta agregan contra etiquetas
   fijas GRUPO 01..GRUPO 30: una fuera de esa lista no aparece en ningún
   bloque, pero sigue contando en el total. */
const ORDINALES = Array.from({ length: 30 }, (_, i) => i + 1);
const etiquetaGrupo = (n) => `GRUPO ${String(n).padStart(2, "0")}`;

const SEV_TONE = { critico: "var(--danger)", alto: "#d9682e", medio: "var(--brand)", bajo: "var(--success)" };

/* Con que criterios nace una agrupacion recien creada. Nada activado: que la
   decision sea explicita y no herede por descuido lo de otra. */
const CRITERIOS_NUEVOS = {
  clasificacion: "complemento_personal",
  normalizable: false, anualizable: false, computa_total: false,
};

function IgualdadCarga() {
  const { session, fireToast } = useIgualdad();

  const [step, setStep] = useState(0);
  const [agrup, setAgrup] = useState(null);
  const [file, setFile] = useState(null);
  const [leyendo, setLeyendo] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  /* Segundos transcurridos mientras se analiza. No es una barra de progreso a
     propósito: el 90 % del tiempo es una única llamada al modelo, opaca, así
     que una barra sería una mentira que se queda clavada cerca del final. Un
     contador que sube no promete nada y demuestra que sigue vivo. */
  const [segundos, setSegundos] = useState(0);
  const [guardando, setGuardando] = useState(false);

  const [carga, setCarga] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [filas, setFilas] = useState([]);
  const [criterios, setCriterios] = useState({});

  // Mapeo propuesto por la cabecera: de dónde salió (IA, heurística o mixto),
  // por qué se propuso cada destino, con qué confianza y qué incidencias de
  // criterio detectó. Se pinta en el paso de totales para que el mapeo
  // degradado nunca se acepte sin revisar.
  const [columnasCabecera, setColumnasCabecera] = useState([]);
  const [sugerenciaMapeo, setSugerenciaMapeo] = useState({});
  const [origen, setOrigen] = useState(null);
  const [motivos, setMotivos] = useState({});
  const [confianzas, setConfianzas] = useState({});
  const [incidenciasCriterio, setIncidenciasCriterio] = useState([]);
  const [gruposVacios, setGruposVacios] = useState([]);
  const [abierta, setAbierta] = useState(null);

  const [histLoading, setHistLoading] = useState(true);
  const [histError, setHistError] = useState(null);
  const [historial, setHistorial] = useState([]);

  const columnas = useMemo(() => carga?.columnas_importe || [], [carga]);
  const incidencias = useMemo(() => carga?.incidencias || [], [carga]);
  const enBorrador = carga?.estado === "borrador";

  /* Agrupaciones en juego. Salen de los conceptos que ya apuntan a una, mas
     las que se crean vacias a la espera de que se les asigne alguno: si solo
     se dedujeran de los conceptos, crear una agrupacion nueva seria imposible
     hasta tener algo dentro. */
  const agrupaciones = useMemo(() => {
    const mapa = new Map();

    for (const g of gruposVacios) {
      if (g.nombre) mapa.set(g.nombre, { nombre: g.nombre, columnas: [], importe: 0, criterios: g });
    }

    for (const col of columnas) {
      const c = criterios[col];
      if (!c?.familia) continue;

      const grupo = mapa.get(c.familia) || { nombre: c.familia, columnas: [], importe: 0, criterios: c };
      grupo.columnas.push(col);
      grupo.importe += Number(resumen?.por_columna?.find((x) => x.columna === col)?.importe || 0);
      // Los criterios del grupo son los del primer concepto que lo estrena:
      // a partir de ahi mandan sobre los que entren.
      grupo.criterios = grupo.columnas.length === 1 ? c : grupo.criterios;
      mapa.set(c.familia, grupo);
    }

    return [...mapa.values()].sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
  }, [criterios, columnas, gruposVacios, resumen]);

  const sinAgrupar = useMemo(
    () => columnas.filter((col) => !criterios[col]?.familia),
    [columnas, criterios]
  );

  const cargarHistorial = useCallback(async () => {
    if (!session.politicaId) { setHistLoading(false); setHistorial([]); return; }
    setHistLoading(true); setHistError(null);
    try {
      const res = await apiGet("/cargas-masivas", { politica_id: session.politicaId, per_page: 50 });
      setHistorial(res.data || []);
    } catch (e) { setHistError(e.message || true); }
    finally { setHistLoading(false); }
  }, [session.politicaId]);

  useEffect(() => { cargarHistorial(); }, [cargarHistorial]);

  async function refrescar(id) {
    const [r, f] = await Promise.all([
      apiGet(`/cargas-masivas/${id}/resumen`),
      apiGet(`/cargas-masivas/${id}/filas`),
    ]);
    setResumen(r.data);
    setFilas(f.data || []);
  }

  useEffect(() => {
    if (!leyendo && !subiendo) { setSegundos(0); return undefined; }

    const tic = setInterval(() => setSegundos((s) => s + 1), 1000);

    return () => clearInterval(tic);
  }, [leyendo, subiendo]);

  async function onFile(ev) {
    const f = ev.target.files?.[0] || null;
    setFile(f);
    if (!f || !session.politicaId) return;

    // Se lee la cabecera para saber qué columnas trae y proponer el mapeo; el
    // fichero no se guarda todavía.
    setLeyendo(true);
    try {
      const cab = await apiUpload("/cargas-masivas/cabecera", { politica_id: session.politicaId }, { fichero: f });
      const mapeo = cab.data?.sugerencia || {};
      setColumnasCabecera(cab.data?.columnas || []);
      setSugerenciaMapeo(mapeo);
      setOrigen(cab.data?.origen || null);
      setMotivos(cab.data?.motivos || {});
      setConfianzas(cab.data?.confianzas || {});
      setIncidenciasCriterio(cab.data?.incidencias_criterio || []);

      setSubiendo(true);
      const res = await apiUpload("/cargas-masivas", { politica_id: session.politicaId, mapeo }, { fichero: f });
      setCarga(res.data);
      setCriterios(res.data?.criterios_concepto || {});
      await refrescar(res.data.id);
      await cargarHistorial();
      setStep(1);
    } catch (e) {
      fireToast(e.errors ? Object.values(e.errors).flat().join(" ") : (e.message || "No se pudo leer el fichero."));
      setFile(null);
    } finally { setLeyendo(false); setSubiendo(false); }
  }

  function plantilla() {
    downloadCsv("plantilla_registro.csv", PLANTILLA, [[
      "S-100999-1", "100999", "M", "2026-01-01", "", "100",
      "Técnico/a de Producción", "Operario/a", "Centro logístico", "28000", "1500", "29500",
    ]]);
    fireToast("Plantilla descargada.");
  }

  /* Corrige una fila. El backend revalida y devuelve la fila actualizada. */
  async function guardarFila(fila, campo, valor) {
    try {
      const res = await apiPatch(`/cargas-masivas/${carga.id}/filas/${fila.id}`, { [campo]: valor });
      setFilas((xs) => xs.map((x) => (x.id === fila.id ? res.data : x)));
      const r = await apiGet(`/cargas-masivas/${carga.id}/resumen`);
      setResumen(r.data);
    } catch (e) {
      fireToast(e.errors ? Object.values(e.errors).flat().join(" ") : (e.message || "No se pudo guardar."));
    }
  }

  /* Corrige un importe suelto de una persona: es lo que arregla un descuadre
     cuando el fichero trae una errata en una columna. */
  function guardarImporte(fila, columna, valor) {
    const actual = fila.conceptos?.[columna];
    const nuevo = valor === "" ? null : Number(valor);
    if (nuevo === (actual ?? null)) return;

    return guardarFila(fila, "conceptos", { [columna]: nuevo });
  }

  /* Recorre los conceptos de una agrupacion aplicandoles un cambio. Las
     agrupaciones no son una lista cerrada: son lo que el usuario decida, y sus
     criterios mandan sobre los de cada concepto suelto. */
  function sobreLosConceptosDe(nombre, transformar) {
    setCriterios((cs) => {
      const siguiente = { ...cs };
      for (const [col, c] of Object.entries(cs)) {
        if (c?.familia === nombre) siguiente[col] = transformar(c);
      }
      return siguiente;
    });
  }

  function crearAgrupacion() {
    const usados = new Set(agrupaciones.map((g) => g.nombre));
    let nombre = "Nueva agrupación";
    for (let n = 2; usados.has(nombre); n++) nombre = `Nueva agrupación ${n}`;

    setGruposVacios((gs) => [...gs, { nombre, ...CRITERIOS_NUEVOS }]);
    fireToast("Agrupación creada. Ponle nombre y asígnale conceptos.");
  }

  /* Renombrar arrastra a sus conceptos. Si el nombre nuevo ya existe, las dos
     agrupaciones se funden en una: es la forma natural de juntar dos que
     resultaron ser la misma. */
  function renombrarAgrupacion(viejo, nuevo) {
    const limpio = nuevo.trim();
    setGruposVacios((gs) => gs.map((g) => (g.nombre === viejo ? { ...g, nombre: limpio } : g)));
    if (limpio === "" || limpio === viejo) return;
    sobreLosConceptosDe(viejo, (c) => ({ ...c, familia: limpio }));
  }

  function fijarCriterioGrupo(nombre, campo, valor) {
    setGruposVacios((gs) => gs.map((g) => (g.nombre === nombre ? { ...g, [campo]: valor } : g)));
    sobreLosConceptosDe(nombre, (c) => ({ ...c, [campo]: valor }));
  }

  /* Deshace la agrupacion sin tocar los conceptos: vuelven a estar sueltos,
     esperando que se decida donde van. */
  function borrarAgrupacion(nombre) {
    setGruposVacios((gs) => gs.filter((g) => g.nombre !== nombre));
    sobreLosConceptosDe(nombre, (c) => ({ ...c, familia: "" }));
    fireToast(`Agrupación «${nombre}» deshecha.`);
  }

  /* Meter un concepto en una agrupacion le impone los criterios de esta: es lo
     que hace que agrupar sirva para algo y no haya que responder por concepto. */
  function asignarAgrupacion(columna, nombre) {
    const grupo = agrupaciones.find((g) => g.nombre === nombre);

    setCriterios((cs) => ({
      ...cs,
      [columna]: {
        ...(cs[columna] || {}),
        familia: nombre,
        ...(grupo ? {
          clasificacion: grupo.criterios.clasificacion,
          normalizable: grupo.criterios.normalizable,
          anualizable: grupo.criterios.anualizable,
          computa_total: grupo.criterios.computa_total,
        } : {}),
      },
    }));
  }

  async function guardarConceptos() {
    if (guardando) return;

    // Los criterios viven en la agrupacion, asi que un concepto suelto se
    // quedaria sin forma de responder por el.
    if (sinAgrupar.length) {
      fireToast(`Falta agrupar ${sinAgrupar.length} concepto(s).`);
      return;
    }

    setGuardando(true);
    try {
      const res = await apiPut(`/cargas-masivas/${carga.id}/conceptos`, { criterios });
      setCarga(res.data);
      await refrescar(carga.id);
      setStep(4);
    } catch (e) {
      fireToast(e.errors ? Object.values(e.errors).flat().join(" ") : (e.message || "No se pudieron guardar los criterios."));
    } finally { setGuardando(false); }
  }

  async function confirmar() {
    if (guardando) return;
    setGuardando(true);
    try {
      const res = await apiPost(`/cargas-masivas/${carga.id}/confirmar`);
      setCarga(res.data);
      await cargarHistorial();
      fireToast(`Registro cargado · ${res.data.n_registros} situaciones.`);
    } catch (e) {
      fireToast(e.message || "No se pudo confirmar.");
    } finally { setGuardando(false); }
  }

  /* Las agrupaciones son de la política, no del lote: una carga puede ser
     parcial y no puede redefinir lo que ya se acordó con la empresa. */
  async function cargarAgrupaciones() {
    try {
      const { data } = await apiGet(`/politicas-retributivas/${session.politicaId}/agrupaciones-registro`);
      setAgrup(data);
    } catch (e) {
      fireToast(e.message || "No se pudieron cargar las agrupaciones.");
    }
  }

  function cambiarOrdinal(grupoConvenioId, ordinal) {
    setAgrup((a) => ({
      ...a,
      clasificacion: a.clasificacion.map((c) =>
        c.grupo_convenio_id === grupoConvenioId ? { ...c, ordinal: Number(ordinal) } : c),
    }));
  }

  function cambiarBanda(puestoId, bandaTivId) {
    setAgrup((a) => ({
      ...a,
      valor: a.valor.map((v) =>
        v.puesto_id === puestoId ? { ...v, banda_tiv_id: bandaTivId ? Number(bandaTivId) : null } : v),
    }));
  }

  async function guardarAgrupaciones() {
    if (guardando || !agrup) return;
    setGuardando(true);
    try {
      const { data } = await apiPost(`/politicas-retributivas/${session.politicaId}/agrupaciones-registro`, {
        clasificacion: agrup.clasificacion.map((c) => ({
          grupo_convenio_id: c.grupo_convenio_id, ordinal: c.ordinal,
        })),
        valor: agrup.valor.filter((v) => v.banda_tiv_id).map((v) => ({
          puesto_id: v.puesto_id,
          banda_tiv_id: v.banda_tiv_id,
          denominacion_valorada: v.denominacion_valorada || null,
        })),
      });
      setAgrup(data);
      fireToast("Correspondencia confirmada.");
    } catch (e) {
      fireToast(e.errors ? Object.values(e.errors).flat().join(" ") : (e.message || "No se pudo guardar."));
    } finally { setGuardando(false); }
  }

  function reset() {
    setStep(0); setFile(null); setCarga(null); setResumen(null); setFilas([]); setCriterios({}); setAgrup(null);
    setColumnasCabecera([]); setOrigen(null); setMotivos({}); setConfianzas({}); setIncidenciasCriterio([]);
  }

  async function revertir(c) {
    try {
      await apiPost(`/cargas-masivas/${c.id}/revertir`);
      fireToast("Carga revertida.");
      await cargarHistorial();
    } catch (e) { fireToast(e.message || "No se pudo revertir."); }
  }

  /* Vuelve a una revision dejada a medias. Sin esto, salir de la pantalla con
     un borrador abierto lo dejaba inalcanzable: ni se confirma ni se tira. */
  async function retomar(c) {
    try {
      const res = await apiGet(`/cargas-masivas/${c.id}`);
      setCarga(res.data);
      setCriterios(res.data?.criterios_concepto || {});
      await refrescar(c.id);
      setStep(1);
    } catch (e) { fireToast(e.message || "No se pudo abrir el borrador."); }
  }

  /* Abandonar una revision a medias. No hay nada que deshacer en el registro,
     asi que el borrador se tira entero en vez de quedarse en el historico. */
  async function descartar(c) {
    try {
      await apiDelete(`/cargas-masivas/${c.id}`);
      fireToast("Borrador descartado.");
      if (carga?.id === c.id) reset();
      await cargarHistorial();
    } catch (e) { fireToast(e.message || "No se pudo descartar el borrador."); }
  }

  function fdate(iso) { try { return new Date(iso).toLocaleString("es-ES"); } catch { return iso; } }

  if (!session.politicaId) {
    return (
      <div className="rb-page">
        <h1 className="rb-h1">Carga del registro</h1>
        <div className="rb-card">
          <SectionHead icon={AlertCircle} title={session.cliente ? "Esta empresa aún no tiene registro" : "Elige primero la empresa"} />
          <p className="rb-note">
            {session.cliente
              ? <>No se ha podido preparar el registro de <b>{session.cliente.nombre}</b>. Suele ser porque tu rol no puede crearlo.</>
              : <>Elige la empresa con la que vas a trabajar. Su registro del ejercicio se prepara solo.</>}
          </p>
          <div className="rb-acciones" style={{ marginTop: 14 }}>
            <button className="rb-btn" onClick={() => navigate("/empresas")}>
              {session.cliente ? "Cambiar de empresa" : "Elegir empresa"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rb-page">
      <h1 className="rb-h1">Carga del registro</h1>

      <nav className="rb-wizard" aria-label="Pasos de la carga">
        {STEPS.map((s, i) => (
          <button
            key={s}
            type="button"
            className={"rb-wstep" + (i === step ? " on" : "") + (i < step ? " done" : "")}
            disabled={i > 0 ? !carga : i > step}
            onClick={() => setStep(i)}
          >
            <span className="rb-wnum">{i < step ? <CheckCircle2 size={15} /> : i + 1}</span>{s}
          </button>
        ))}
      </nav>

      {/* ---------- 1 · Subir ---------- */}
      {step === 0 ? (
        <div className="rb-card">
          <div className="rb-drop">
            <UploadCloud size={36} />
            <div>
              <b>Sube el Excel / CSV del registro</b>
              <p>
                Una fila por situación contractual, con los datos de la persona, su puesto y una
                columna por concepto retributivo. <b>Nada se crea al subir</b>: primero lo revisas.
              </p>
            </div>
            <div className="rb-drop-actions">
              <label className="rb-btn" style={{ cursor: "pointer" }} htmlFor="fichero-carga">
                {(leyendo || subiendo) ? <Loader2 size={14} className="rb-spin" /> : null}
                {leyendo ? `Analizando… ${segundos} s` : subiendo ? `Preparando… ${segundos} s` : "Seleccionar fichero"}
              </label>
              <input id="fichero-carga" type="file" accept=".csv,.txt,.xlsx,.xls" style={{ display: "none" }} onChange={onFile} />
              <button className="rb-btn-ghost" onClick={plantilla}><Download size={14} /> Plantilla</button>
            </div>
            {file ? <p className="rb-note">{file.name}</p> : null}
            {(leyendo || subiendo) ? (
              <p className="rb-note">
                El análisis suele tardar entre 20 y 60 segundos, y no depende del número
                de personas: una plantilla de 20.000 tarda lo mismo que una de 30.
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* ---------- 2 · Totales ---------- */}
      {step === 1 && resumen ? (
        <>
          <div className="rb-kpis rb-kpis-4">
            <div className="rb-kpi">
              <div className="rb-kpi-l">Situaciones</div>
              <div className="rb-kpi-v">{resumen.n_filas}</div>
              <div className="rb-kpi-s">de {resumen.n_personas} personas</div>
            </div>
            <div className="rb-kpi">
              <div className="rb-kpi-l">Masa retributiva</div>
              <div className="rb-kpi-v">{eur(resumen.masa_retributiva)}</div>
              <div className="rb-kpi-s">suma de los conceptos</div>
            </div>
            <div className="rb-kpi">
              <div className="rb-kpi-l">Descuadres</div>
              <div className="rb-kpi-v" style={{ color: resumen.n_descuadres ? "var(--danger)" : "var(--success)" }}>
                {resumen.n_descuadres}
              </div>
              <div className="rb-kpi-s">no cuadra con el total declarado</div>
            </div>
            <div className="rb-kpi">
              <div className="rb-kpi-l">Jornada o periodo parcial</div>
              <div className="rb-kpi-v">{resumen.n_situacion_parcial}</div>
              <div className="rb-kpi-s">se equiparan al comparar</div>
            </div>
          </div>

          {resumen.total_declarado !== null ? (
            <div
              className="rb-resbanner"
              style={{
                background: Math.abs(resumen.total_declarado - resumen.masa_retributiva) < 1 ? "var(--success-bg)" : "var(--danger-bg)",
                color: Math.abs(resumen.total_declarado - resumen.masa_retributiva) < 1 ? "var(--success-700)" : "var(--danger-700)",
              }}
            >
              {Math.abs(resumen.total_declarado - resumen.masa_retributiva) < 1
                ? <><CheckCircle2 size={16} /> El fichero cuadra: los conceptos suman lo que declara el total.</>
                : <><AlertCircle size={16} /> Los conceptos suman {eur(resumen.masa_retributiva)} y el total declarado es {eur(resumen.total_declarado)}. Diferencia de {eur(Math.abs(resumen.total_declarado - resumen.masa_retributiva))}.</>}
            </div>
          ) : null}

          {origen === "heuristica" ? (
            <div className="rb-warn">
              Propuesta automática no disponible: el mapeo viene de las reglas de siempre.
              Revísalo columna a columna.
            </div>
          ) : null}
          {origen === "mixto" ? (
            <div className="rb-warn">
              Parte del mapeo se ha propuesto con las reglas de siempre porque la propuesta
              automática no era válida para alguna columna.
            </div>
          ) : null}

          {columnasCabecera.length ? (
            <div className="rb-card rb-card-flat">
              <div style={{ padding: "14px 16px" }}><SectionHead icon={Layers} title="Mapeo propuesto" /></div>
              <div style={{ overflowX: "auto" }}>
                <table className="rb-table">
                  <thead><tr><th className="rb-l">Columna del fichero</th><th className="rb-l">Destino</th><th className="rb-l">Motivo</th></tr></thead>
                  <tbody>
                    {columnasCabecera.map((col) => (
                      <tr key={col}>
                        <td className="rb-l rb-mono">{col}</td>
                        <td className="rb-l rb-mono">
                          {sugerenciaMapeo[col] || <span className="rb-soft">Sin destino asignado</span>}
                        </td>
                        <td className="rb-l">
                          <div className="rb-soft rb-xs">
                            {motivos[col]
                              ? <>{motivos[col]}{confianzas[col] ? ` · confianza ${confianzas[col]}` : null}</>
                              : "Propuesto por las reglas de siempre, sin explicación del modelo."}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          <div className="rb-card rb-card-flat">
            <div style={{ padding: "14px 16px" }}><SectionHead icon={Layers} title="Conceptos que trae el fichero" /></div>
            <div style={{ overflowX: "auto" }}>
              <table className="rb-table">
                <thead><tr><th className="rb-l">Columna</th><th className="rb-l">Concepto</th><th>Personas</th><th>Importe</th></tr></thead>
                <tbody>
                  {resumen.por_columna.map((c) => (
                    <tr key={c.columna}>
                      <td className="rb-l rb-mono">{c.columna}</td>
                      <td className="rb-l">{c.concepto}</td>
                      <td className="rb-mono">{c.personas}</td>
                      <td className="rb-mono">{eur(c.importe)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {incidencias.length ? (
            <div className="rb-card">
              <SectionHead icon={AlertCircle} title={`Incidencias · ${incidencias.length}`} />
              <div className="rb-valid">
                {incidencias.slice(0, 40).map((v, i) => (
                  <div key={i} className="rb-validrow">
                    <span className="rb-validn" style={{ background: SEV_TONE[v.severidad] || "var(--text-muted)" }}>{v.linea || "—"}</span>
                    <div><b>{v.tipo}</b><p>{v.severidad}{v.referencia ? ` · ${v.referencia}` : ""}</p></div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {incidenciasCriterio.length ? (
            <div className="rb-card">
              <SectionHead icon={AlertTriangle} title={`Incidencias de criterio · ${incidenciasCriterio.length}`} />
              <p className="rb-soft" style={{ margin: "0 0 12px" }}>
                Ninguna de estas da error al importar: falsean el resultado en silencio.
                Corrígelas en el fichero o documéntalas antes de continuar.
              </p>
              <table className="rb-table rb-table-compact">
                <thead><tr><th className="rb-l">Nivel</th><th className="rb-l">Columna</th><th className="rb-l">Detalle</th></tr></thead>
                <tbody>
                  {incidenciasCriterio.map((i, n) => (
                    <tr key={`${i.codigo}-${n}`} className={i.nivel === "Crítica" ? undefined : "is-aviso"}>
                      <td className="rb-l">{i.nivel}</td>
                      <td className="rb-l">{i.campo}</td>
                      <td className="rb-l">{i.detalle}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <div className="rb-acciones">
            <button className="rb-btn-ghost" onClick={reset}>Descartar</button>
            <button className="rb-btn" onClick={() => setStep(2)}>Revisar situaciones <ChevronRight size={14} /></button>
          </div>
        </>
      ) : null}

      {/* ---------- 3 · Situaciones ---------- */}
      {step === 2 ? (
        <>
          <div className="rb-card rb-card-flat">
            <div style={{ padding: "14px 16px" }}>
              <SectionHead icon={Users} title={`Situaciones contractuales · ${filas.length}`} />
              <p className="rb-note">
                La jornada y las fechas deciden cómo se equipara la retribución de cada persona.
                Corrígelas aquí: todavía no ha entrado nada.
              </p>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table className="rb-table">
                <thead><tr>
                  <th className="rb-l">Situación</th><th className="rb-l">Persona</th><th className="rb-c">Sexo</th>
                  <th style={{ width: 90 }}>Jornada %</th><th className="rb-l" style={{ width: 140 }}>Inicio</th>
                  <th className="rb-l" style={{ width: 140 }}>Fin</th><th className="rb-l">Puesto</th><th>Importe</th><th></th>
                </tr></thead>
                <tbody>
                  {filas.map((f) => (
                    <React.Fragment key={f.id}>
                    <tr className={f.incidencias?.length ? "is-aviso" : ""}>
                      <td className="rb-l rb-mono">{f.codigo_externo || `L${f.linea}`}</td>
                      <td className="rb-l rb-mono">{f.persona_codigo}</td>
                      <td className="rb-c">{f.sexo || "—"}</td>
                      <td>
                        <input
                          className="rb-inp rb-inp-mini rb-inp-num" type="number" min="0" max="100"
                          aria-label={`Jornada de ${f.persona_codigo}`}
                          defaultValue={f.pct_jornada ?? ""}
                          onBlur={(e) => guardarFila(f, "pct_jornada", e.target.value === "" ? null : Number(e.target.value))}
                        />
                      </td>
                      <td className="rb-l">
                        <input className="rb-inp rb-inp-mini" type="date" aria-label={`Inicio de ${f.persona_codigo}`}
                          defaultValue={f.fecha_inicio || ""}
                          onBlur={(e) => guardarFila(f, "fecha_inicio", e.target.value || null)} />
                      </td>
                      <td className="rb-l">
                        <input className="rb-inp rb-inp-mini" type="date" aria-label={`Fin de ${f.persona_codigo}`}
                          defaultValue={f.fecha_fin || ""}
                          onBlur={(e) => guardarFila(f, "fecha_fin", e.target.value || null)} />
                      </td>
                      <td className="rb-l">{f.puesto}</td>
                      <td className="rb-mono">
                        {eur(f.suma_conceptos)}
                        {f.descuadre ? <><br /><span className="rb-chip" style={{ background: "var(--danger-bg)", color: "var(--danger)" }}>descuadre {eur(f.descuadre)}</span></> : null}
                      </td>
                      <td>
                        <button className="rb-justb"
                          aria-label={`${abierta === f.id ? "Cerrar" : "Ver"} los importes de ${f.persona_codigo}`}
                          onClick={() => setAbierta((x) => (x === f.id ? null : f.id))}>
                          {abierta === f.id ? "Cerrar" : "Importes"}
                        </button>
                      </td>
                    </tr>
                    {abierta === f.id ? (
                      <tr>
                        <td colSpan={9} style={{ background: "var(--gray-50)" }}>
                          <div className="rb-importes">
                            {columnas.map((col) => (
                              <label key={col} className="rb-importe">
                                <span>{criterios[col]?.concepto || col}</span>
                                <input className="rb-inp rb-inp-mini rb-inp-num" type="number" step="0.01"
                                  aria-label={`${criterios[col]?.concepto || col} de ${f.persona_codigo}`}
                                  defaultValue={f.conceptos?.[col] ?? ""}
                                  onBlur={(e) => guardarImporte(f, col, e.target.value)} />
                              </label>
                            ))}
                          </div>
                          <p className="rb-note" style={{ padding: "0 4px 10px" }}>
                            {f.total_declarado != null
                              ? <>El fichero declara {eur(f.total_declarado)} y estos importes suman {eur(f.suma_conceptos)}.{f.descuadre ? ` Faltan ${eur(f.descuadre)}.` : " Cuadra."}</>
                              : "El fichero no declara un total para esta situación."}
                          </p>
                        </td>
                      </tr>
                    ) : null}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rb-acciones">
            <button className="rb-btn-ghost" onClick={() => setStep(1)}>Totales</button>
            <button className="rb-btn" onClick={() => setStep(3)}>Decidir conceptos <ChevronRight size={14} /></button>
          </div>
        </>
      ) : null}

      {/* ---------- 4 · Conceptos ---------- */}
      {step === 3 ? (
        <>
          <div className="rb-card rb-card-flat">
            <div style={{ padding: "14px 16px" }}>
              <SectionHead icon={Layers} title={`Agrupaciones · ${agrupaciones.length}`}
                right={<button className="rb-btn-ghost" onClick={crearAgrupacion}>+ Nueva agrupación</button>} />
              <p className="rb-note">
                Las agrupaciones las decides tú: crea las que necesites y responde una sola vez por
                cada una. Esto no viene en el fichero — en la herramienta del Ministerio se rellena a
                mano — y es lo que determina cómo se equipara cada importe.
              </p>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table className="rb-table">
                <thead><tr>
                  <th className="rb-l" style={{ width: 220 }}>Agrupación</th>
                  <th>Conceptos</th><th>Importe</th>
                  <th className="rb-l" style={{ width: 170 }}>Clasificación</th>
                  <th className="rb-c">Normaliza<br />por jornada</th>
                  <th className="rb-c">Eleva<br />al periodo</th>
                  <th className="rb-c">Computa<br />al total</th>
                  <th></th>
                </tr></thead>
                <tbody>
                  {agrupaciones.map((g) => (
                    <tr key={g.nombre}>
                      <td className="rb-l">
                        <input className="rb-inp rb-inp-mini" aria-label={`Nombre de la agrupación ${g.nombre}`}
                          defaultValue={g.nombre} key={g.nombre}
                          onBlur={(e) => renombrarAgrupacion(g.nombre, e.target.value)} />
                      </td>
                      <td className="rb-mono">{g.columnas.length}</td>
                      <td className="rb-mono">{eur(g.importe)}</td>
                      <td className="rb-l">
                        <select className="rb-inp rb-inp-mini" aria-label={`Clasificación de ${g.nombre}`}
                          value={g.criterios.clasificacion || "complemento_personal"}
                          onChange={(e) => fijarCriterioGrupo(g.nombre, "clasificacion", e.target.value)}>
                          {CLASIFICACIONES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      </td>
                      <td className="rb-c">
                        <Toggle label={`Normaliza por jornada ${g.nombre}`} on={Boolean(g.criterios.normalizable)}
                          onChange={(v) => fijarCriterioGrupo(g.nombre, "normalizable", v)} />
                      </td>
                      <td className="rb-c">
                        <Toggle label={`Eleva al periodo ${g.nombre}`} on={Boolean(g.criterios.anualizable)}
                          onChange={(v) => fijarCriterioGrupo(g.nombre, "anualizable", v)} />
                      </td>
                      <td className="rb-c">
                        <Toggle label={`Computa al total ${g.nombre}`} on={Boolean(g.criterios.computa_total)}
                          onChange={(v) => fijarCriterioGrupo(g.nombre, "computa_total", v)} />
                      </td>
                      <td>
                        <button className="rb-justb" aria-label={`Deshacer la agrupación ${g.nombre}`}
                          onClick={() => borrarAgrupacion(g.nombre)}><Trash2 size={12} /> Deshacer</button>
                      </td>
                    </tr>
                  ))}
                  {!agrupaciones.length ? <tr><td className="rb-l rb-soft" colSpan={8}>Crea la primera agrupación para empezar.</td></tr> : null}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rb-card rb-card-flat">
            <div style={{ padding: "14px 16px" }}>
              <SectionHead icon={Coins} title={`Conceptos del fichero · ${columnas.length}`} />
              {sinAgrupar.length ? (
                <p className="rb-note">
                  Quedan {sinAgrupar.length} concepto(s) sin agrupar. Sin agrupación no se sabe cómo
                  tratar su importe.
                </p>
              ) : null}
            </div>
            <div style={{ overflowX: "auto" }}>
              <table className="rb-table">
                <thead><tr>
                  <th className="rb-l">Concepto</th><th>Importe</th>
                  <th className="rb-l" style={{ width: 260 }}>Agrupación</th>
                </tr></thead>
                <tbody>
                  {columnas.map((col) => {
                    const c = criterios[col] || {};
                    const peso = resumen?.por_columna?.find((x) => x.columna === col);
                    return (
                      <tr key={col} className={c.familia ? undefined : "is-aviso"}>
                        <td className="rb-l">
                          <b>{c.concepto || col}</b>
                          {c.concepto && c.concepto !== col ? <><br /><span className="rb-mono rb-soft" style={{ fontSize: 11 }}>{col}</span></> : null}
                        </td>
                        <td className="rb-mono">{eur(peso?.importe)}</td>
                        <td className="rb-l">
                          <select className="rb-inp rb-inp-mini" aria-label={`Agrupación de ${c.concepto || col}`}
                            value={c.familia || ""}
                            onChange={(e) => asignarAgrupacion(col, e.target.value)}>
                            <option value="">— Sin agrupar —</option>
                            {agrupaciones.map((g) => <option key={g.nombre} value={g.nombre}>{g.nombre}</option>)}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rb-acciones">
            <button className="rb-btn-ghost" onClick={() => setStep(2)}>Situaciones</button>
            <button className="rb-btn" disabled={guardando} onClick={guardarConceptos}>
              {guardando ? <Loader2 size={14} className="rb-spin" /> : null} Guardar y continuar <ChevronRight size={14} />
            </button>
          </div>
        </>
      ) : null}

      {/* ---------- 5 · Confirmar ---------- */}
      {step === 4 && carga ? (
        <div className="rb-card">
          <SectionHead icon={FileCheck2} title={enBorrador ? "Confirmar la carga" : "Carga confirmada"} />
          <div
            className="rb-resbanner"
            style={{
              background: enBorrador ? "var(--orange-50)" : "var(--success-bg)",
              color: enBorrador ? "var(--brand-hover)" : "var(--success-700)",
            }}
          >
            {enBorrador
              ? <><AlertCircle size={16} /> Nada ha entrado todavía. Al confirmar se crean las personas, sus situaciones y sus importes.</>
              : <><CheckCircle2 size={16} /> {carga.n_registros} situaciones incorporadas al registro.</>}
          </div>

          <table className="rb-table rb-table-compact">
            <tbody>
              <tr><td className="rb-l">Situaciones</td><td className="rb-mono">{resumen?.n_filas ?? carga.n_registros}</td></tr>
              <tr><td className="rb-l">Personas</td><td className="rb-mono">{resumen?.n_personas ?? "—"}</td></tr>
              <tr><td className="rb-l">Conceptos</td><td className="rb-mono">{columnas.length}</td></tr>
              <tr><td className="rb-l">Masa retributiva</td><td className="rb-mono">{eur(resumen?.masa_retributiva)}</td></tr>
              <tr><td className="rb-l">Incidencias</td><td className="rb-mono">{carga.n_incidencias}</td></tr>
            </tbody>
          </table>

          <div className="rb-acciones" style={{ marginTop: 16 }}>
            <button className="rb-btn-ghost" onClick={() => setStep(3)}>Conceptos</button>
            {enBorrador
              ? <button className="rb-btn" disabled={guardando} onClick={confirmar}>
                  {guardando ? <Loader2 size={14} className="rb-spin" /> : null} Confirmar e incorporar
                </button>
              : <button className="rb-btn" onClick={() => { setStep(5); cargarAgrupaciones(); }}>
                  Revisar agrupaciones <ChevronRight size={14} />
                </button>}
          </div>
        </div>
      ) : null}

      {/* ---------- 6 · Agrupaciones ----------
          Columnas 22 y 23 de la plantilla oficial. El procedimiento no admite
          deducirlas: las denominaciones de nómina y las valoradas casi nunca
          coinciden, y dos grupos mal emparejados se suman como un solo
          colectivo sin que nada avise. */}
      {step === 5 ? (
        <>
          <div className="rb-card">
            <SectionHead icon={Layers} title="Agrupación profesional" />
            <p className="rb-soft" style={{ margin: "0 0 12px" }}>
              Columna AGRUP. CLAS. PROF. del Registro. Mientras no se confirme, el entregable la marca como
              propuesta sin revisar y abre una incidencia.
            </p>
            {!agrup ? <Status loading /> : agrup.clasificacion.length === 0 ? (
              <p className="rb-soft">Ninguna situación tiene grupo de convenio asignado, así que no hay nada que agrupar todavía.</p>
            ) : (
              <table className="rb-table rb-table-compact">
                <thead><tr>
                  <th className="rb-l">Grupo de convenio</th><th>Situaciones</th>
                  <th className="rb-l">Agrupación</th><th>Estado</th>
                </tr></thead>
                <tbody>
                  {agrup.clasificacion.map((c) => (
                    <tr key={c.grupo_convenio_id} className={c.confirmada ? undefined : "is-aviso"}>
                      <td className="rb-l"><b>{c.nombre}</b> <span className="rb-mono rb-soft">{c.codigo}</span></td>
                      <td className="rb-mono">{c.situaciones}</td>
                      <td className="rb-l">
                        <select className="rb-inp rb-inp-mini" aria-label={`Agrupación de ${c.nombre}`}
                          value={c.ordinal}
                          onChange={(e) => cambiarOrdinal(c.grupo_convenio_id, e.target.value)}>
                          {ORDINALES.map((n) => <option key={n} value={n}>{etiquetaGrupo(n)}</option>)}
                        </select>
                      </td>
                      <td>{c.confirmada
                        ? <span className="rb-chip"><CheckCircle2 size={11} /> Confirmada</span>
                        : <span className="rb-chip" style={{ background: "var(--orange-50)", color: "var(--brand-hover)" }}>Propuesta</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {agrup?.tiene_svpt ? (
            <div className="rb-card">
              <SectionHead icon={Layers} title="Correspondencia puestos-valor" />
              <p className="rb-soft" style={{ margin: "0 0 12px" }}>
                Columna AGRUP. VALOR. PTO. Revísala puesto a puesto: el nombre de nómina y el del informe de
                valoración rara vez coinciden.
              </p>
              <table className="rb-table rb-table-compact">
                <thead><tr>
                  <th className="rb-l">Puesto-empresa</th><th>Puntos</th><th>Situaciones</th>
                  <th className="rb-l">Banda de valoración</th><th>Estado</th>
                </tr></thead>
                <tbody>
                  {agrup.valor.map((v) => (
                    <tr key={v.puesto_id} className={v.confirmada ? undefined : "is-aviso"}>
                      <td className="rb-l"><b>{v.nombre}</b></td>
                      <td className="rb-mono">{v.pts}</td>
                      <td className="rb-mono">{v.situaciones}</td>
                      <td className="rb-l">
                        <select className="rb-inp rb-inp-mini" aria-label={`Banda de ${v.nombre}`}
                          value={v.banda_tiv_id || ""}
                          onChange={(e) => cambiarBanda(v.puesto_id, e.target.value)}>
                          <option value="">— Sin banda —</option>
                          {agrup.bandas.map((b) => (
                            <option key={b.id} value={b.id}>{b.etiqueta} · {b.nombre}</option>
                          ))}
                        </select>
                      </td>
                      <td>{v.confirmada
                        ? <span className="rb-chip"><CheckCircle2 size={11} /> Confirmada</span>
                        : <span className="rb-chip" style={{ background: "var(--orange-50)", color: "var(--brand-hover)" }}>Propuesta</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <div className="rb-acciones">
            <button className="rb-btn-ghost" onClick={() => setStep(4)}>Confirmar</button>
            <button className="rb-btn" disabled={guardando || !agrup} onClick={guardarAgrupaciones}>
              {guardando ? <Loader2 size={14} className="rb-spin" /> : null} Confirmar correspondencia
            </button>
          </div>
        </>
      ) : null}

      {/* ---------- Histórico ---------- */}
      <div className="rb-card rb-card-flat" style={{ marginTop: 18 }}>
        <div style={{ padding: "14px 16px" }}><SectionHead icon={Clock} title="Histórico de cargas" /></div>
        {histLoading || histError ? <Status loading={histLoading} error={histError} /> : (
          <table className="rb-table">
            <thead><tr><th className="rb-l">Fecha</th><th className="rb-l">Fichero</th><th className="rb-l">Usuario</th><th>Registros</th><th>Incidencias</th><th>Estado</th><th></th></tr></thead>
            <tbody>
              {historial.map((c) => (
                <tr key={c.id}>
                  <td className="rb-l rb-mono">{fdate(c.created_at)}</td>
                  <td className="rb-l">{c.fichero_nombre}</td>
                  <td className="rb-l rb-soft">{c.usuario?.name || "—"}</td>
                  <td className="rb-mono">{c.n_registros}</td>
                  <td className="rb-mono">{c.n_incidencias}</td>
                  <td><span className="rb-chip" style={{ background: c.estado === "activa" ? "var(--orange-50)" : "var(--gray-100)", color: c.estado === "activa" ? "var(--brand-hover)" : "var(--text-muted)" }}>{c.estado_label || c.estado}</span></td>
                  <td>
                    {c.reversible && c.estado === "activa"
                      ? <button className="rb-justb" onClick={() => revertir(c)}><RotateCcw size={12} /> Revertir</button>
                      : null}
                    {c.estado === "borrador"
                      ? <>
                          <button className="rb-justb" onClick={() => retomar(c)}>Retomar</button>{" "}
                          <button className="rb-justb" aria-label={`Descartar el borrador de ${c.fichero_nombre}`}
                            onClick={() => descartar(c)}><Trash2 size={12} /> Descartar</button>
                        </>
                      : null}
                  </td>
                </tr>
              ))}
              {!historial.length ? <tr><td className="rb-l rb-soft" colSpan={7}>Aún no hay cargas registradas.</td></tr> : null}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export { IgualdadCarga };
